import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { PlusCircle } from "lucide-react";

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  value: string;
  setValue: (s: string) => void;
  title: string;
  setTitle: (s: string) => void;
  onConfirm: () => Promise<void> | void;
  t: (k: string) => string;
  disabled?: boolean;
}

const QuickMessageDialog: React.FC<Props> = ({
  open,
  setOpen,
  value,
  setValue,
  title,
  setTitle,
  onConfirm,
  t,
  disabled = false,
}) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent className="bg-white max-w-md w-full rounded-lg shadow-lg">
      <DialogHeader>
        <DialogTitle className="flex gap-2 items-center">
          <PlusCircle className="w-4 h-4 text-[#0064E0]" />
          <span className="text-lg text-[#13161A] font-normal">
            {t("add_quick_message")}
          </span>
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#252A32] mb-2">
            {t("title")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full border border-[#E2E4E9] rounded-lg p-2 bg-white text-sm font-normal placeholder:text-[#6F7D95] focus:outline-none focus:ring-1 focus:ring-[#0064E0]"
            placeholder={t("quick_message_title_placeholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            required
          />
          {!title.trim() && (
            <p className="text-xs text-red-500 mt-1">{t("title_required")}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#252A32] mb-2">
            {t("message")} <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-[#E2E4E9] rounded-lg p-2 resize-none h-44 bg-white text-sm font-normal placeholder:text-[#6F7D95] focus:outline-none focus:ring-1 focus:ring-[#0064E0] whitespace-pre-wrap"
            placeholder={t("type_quick_message_placeholder")}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={500}
            required
          />
          {!value.trim() && (
            <p className="text-xs text-red-500 mt-1">{t("message_required")}</p>
          )}
        </div>
      </div>

      <DialogFooter className="flex justify-end gap-2">
        <DialogClose asChild>
          <Button variant="secondary" className="bg-white border rounded-lg px-4 py-2">
            {t("cancel")}
          </Button>
        </DialogClose>
        <Button
          onClick={onConfirm}
          className="bg-[#105BDB] text-white hover:bg-[#679BF4] active:bg-[#0B2568] px-4 py-2 text-base font-normal rounded-lg"
          disabled={disabled || !value.trim() || !title.trim()}
        >
          {t("confirm")}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default QuickMessageDialog;
